/**
 * What a plan does not include, and what it would do.
 *
 * The application drew this with a chartreuse "CONTENT MONITORING" eyebrow
 * above a 36px serif title, a navy tile beside it holding six invented bars
 * captioned "Signal preview: Ready to unlock", and three bullets each in
 * their own cream pill with a chartreuse dot.
 *
 * The bars are the part that mattered. They are not this customer's data,
 * they are not anyone's data, and a picture of a number that does not exist
 * is the worst thing to show somebody at the moment you are asking them to
 * pay. It is gone, along with the eyebrow that named our feature rather than
 * their problem.
 */
import type React from 'react'
import { Button, Dialog } from '@/design-system/primitives'
import { NeedsPlan } from '@/design-system/marks'
import '@/design-system/marks.css'
import '@/design-system/pagedetail.css'

export type UnlockFeatureKey = 'bot-tracking' | 'schema' | 'monitoring'

const unlockFeatureContent: Record<UnlockFeatureKey, {
  title: string
  description: string
  bullets: string[]
}> = {
  'bot-tracking': {
    title: 'See which AI systems read your site',
    description:
      'Assistants send their own readers round your site the way search engines always have. This tells you which ones came, how often, and which pages they read.',
    bullets: [
      'Which assistants are reading your site, named.',
      'Whether they come back, and how often.',
      'Which of your pages they actually read.',
    ],
  },
  schema: {
    title: 'Tell assistants what your business is',
    description:
      'Markup is how an assistant knows what you sell, who you sell it to and what your pages are for. We write it from your own page and keep it lined up with what is on it.',
    bullets: [
      'Markup written from the page you already have.',
      'Plain-text copies for the assistants that want words.',
      'Kept in step with what your page actually says.',
    ],
  },
  monitoring: {
    title: 'Know when a page stops matching what we said about it',
    description:
      'Pages get edited. When one changes underneath the markup we wrote, what assistants are being told stops being true, and nothing on the page shows it.',
    bullets: [
      'We check your pages and tell you when one changes.',
      'You find out before your customers do.',
      'The markup goes back in step with the page in a click.',
    ],
  },
}

export function FeatureUnlockModal({
  feature,
  domainName,
  onClose,
  onUpgrade,
}: {
  feature: UnlockFeatureKey
  domainName: string
  onClose: () => void
  onUpgrade: () => void
}) {
  const content = unlockFeatureContent[feature]

  const handleUpgrade = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onUpgrade()
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={content.title}
      lead={content.description}
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Maybe later
          </Button>
          <Button onClick={handleUpgrade}>View subscription plans</Button>
        </>
      }
    >
      <div className="ds-unlock">
        <div className="ds-unlock__plan">
          <NeedsPlan plan="a paid plan" />
          <span className="ds-unlock__for">For {domainName}</span>
        </div>
        <ul className="ds-unlock__list">
          {content.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </div>
    </Dialog>
  )
}
